import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { useRowSelect, useFilters, useGlobalFilter, useSortBy, useTable } from "react-table";
import { Dimmer, Loader, Modal, Label, Checkbox, Header, Icon, Table } from "semantic-ui-react";
import { handleFineGranularityChange } from "../../../../actions/rolesPermissions/rolesPermissions";
import { getLayers } from "../../../../store/layers";
import { exists } from "../../../../utils/utils";
import { DefaultColumnFilter, GlobalFilter } from "../../../common/SearchableTable";

const FineGranularityModal = ({
  layers,
  loading,
  handleFineGranularityChange,
  getLayers,
  canEdit,
  onActionChange,
  checked,
  role,
  action,
  fineGranularity,
  fineGrainedPermission,
  fineGranularityChanges
}) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => {
    if (!canEdit) return;
    onActionChange(action.id, !checked);
  };

  const filterTypesConditions = useMemo(
    () => ({
      layer: (rowValue, filterValue) => {
        return String(rowValue.name)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      }
    }),
    []
  );

  const booleanSort = useMemo(
    () => current => {
      return current.isSelected ? -1 : 1;
    },
    []
  );

  const checkboxStyle = useMemo(
    () => ({
      marginLeft: "40%"
    }),
    []
  );
  const columns = useMemo(
    () => [
      {
        id: "checkbox",
        accessor: "checkbox",
        // eslint-disable-next-line react/display-name
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <Checkbox
            disabled={!canEdit}
            {...getToggleAllRowsSelectedProps()}
            style={checkboxStyle}
            onClick={event => canEdit && event.stopPropagation()}
          />
        ),
        checkbox: true,
        disableFilters: true,
        sortType: booleanSort,
        // eslint-disable-next-line react/display-name
        Cell: ({ row }) => (
          <Checkbox
            disabled={!canEdit}
            {...row.getToggleRowSelectedProps()}
            style={checkboxStyle}
            onClick={event => canEdit && event.stopPropagation()}
          />
        )
      },
      {
        accessor: "layer",
        Header: "Layer",
        Cell: ({ value }) => value.name,
        filter: "layer"
      }
    ],
    [checkboxStyle, canEdit, booleanSort]
  );

  const filterTypes = useMemo(
    () =>
      Object.keys(filterTypesConditions).reduce((filterTypes, type) => {
        const condition = filterTypesConditions[type];
        const newFilterTypeFunction = (rows, id, filterValue) => {
          return rows.filter(row => {
            const rowValue = row.values[id];
            return exists(rowValue) ? condition(rowValue, filterValue) : false;
          });
        };
        return Object.assign(filterTypes, { [type]: newFilterTypeFunction });
      }, {}),
    [filterTypesConditions]
  );

  const globalFilter = useMemo(
    () => (rows, ids, filterValue) => {
      return rows.filter(row =>
        row.cells.reduce(
          (recursion, cell) =>
            recursion ||
            (cell.column &&
              !cell.column.disableFilters &&
              (!exists(filterTypesConditions[cell.column.filter]) ||
                filterTypesConditions[cell.column.filter](cell.value, filterValue))),
          false
        )
      );
    },
    [filterTypesConditions]
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  const data = useMemo(
    () =>
      layers.map(layer => ({
        checkbox: layer.id,
        layer
      })),
    [layers]
  );
  const getRowId = useMemo(() => ({ layer }) => layer.id, []);
  const exisitingChanges = fineGranularityChanges.find(
    change => change.role === role.id && change.fineGrainedPermission === fineGrainedPermission.id
  );

  const selectedIds = exisitingChanges?.ids || fineGranularity?.ids || [];
  const selectedRowIds = selectedIds.reduce(
    (object, id) => Object.assign(object, { [id]: true }),
    {}
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      globalFilter,
      filterTypes,
      getRowId,
      initialState: { selectedRowIds }
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useRowSelect
  );

  const isFilterResult = (cellValue, filterCondition, filterValue) => {
    return filterCondition(cellValue, filterValue);
  };
  // Render the UI for your table
  return (
    <Modal
      closeIcon
      open={open}
      trigger={
        <Label
          className="tooltip "
          as="a"
          basic
          color="blue"
          style={{ float: "right", border: 0, padding: 0 }}
        >
          {checked
            ? `${selectedIds && selectedIds.length > 0 ? selectedIds.length : "all"}  enabled`
            : "disabled"}
          <span className="tooltiptext">Click to enable or disable specific map layers</span>
        </Label>
      }
      onClose={() => {
        handleFineGranularityChange({
          role: role.id,
          fineGrainedPermission: fineGrainedPermission.id,
          ids: Object.keys(state.selectedRowIds).map(id => parseInt(id))
        });
        setOpen(false);
      }}
      onOpen={() => {
        getLayers();
        setOpen(true);
      }}
    >
      <Header>
        <Icon name="map outline" />
        <Header.Content style={{ width: "100%" }}>
          Layers enabled
          <Checkbox
            aria-label="Enable layer checkbox"
            disabled={!canEdit}
            style={{ float: "right", paddingRight: "10%" }}
            checked={checked}
            onClick={handleClick}
            toggle
          />
        </Header.Content>
      </Header>
      <Modal.Content>
        {loading ? (
          <Dimmer active>
            <Loader />
          </Dimmer>
        ) : (
          <>
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={state.globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
            <div style={{ maxHeight: "400px", overflow: "auto" }}>
              <Table celled {...getTableProps()}>
                <Table.Header>
                  {headerGroups.map((headerGroup, headerIndex) => (
                    <Table.Row key={headerIndex} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, headerCellIndex) => (
                        <Table.HeaderCell
                          key={headerCellIndex}
                          width={column.checkbox && 2}
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          {column.render("Header")}
                          <span style={{ fontSize: "large" }}>
                            {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                          </span>
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Header>

                <Table.Body {...getTableBodyProps()}>
                  {rows.map((row, index) => {
                    prepareRow(row);
                    return (
                      <Table.Row key={index} {...row.getRowProps()}>
                        {row.cells.map((cell, cellIndex) => {
                          return (
                            <Table.Cell
                              key={cellIndex}
                              {...cell.getCellProps()}
                              positive={
                                !cell.column.disableFilters &&
                                isFilterResult(
                                  cell.value,
                                  filterTypesConditions[cell.column.filter],
                                  state.globalFilter
                                )
                              }
                              onClick={
                                cell.column.checkbox &&
                                (event => {
                                  if (!canEdit) return;
                                  event.stopPropagation();
                                  row.toggleRowSelected();
                                })
                              }
                              style={cell.column.checkbox && { cursor: "pointer" }}
                            >
                              {cell.render("Cell")}
                            </Table.Cell>
                          );
                        })}
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </div>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

FineGranularityModal.propTypes = {
  canEdit: PropTypes.bool,
  onActionChange: PropTypes.func,
  checked: PropTypes.bool,
  role: PropTypes.object,
  resource: PropTypes.object,
  action: PropTypes.object,
  fineGranularity: PropTypes.object
};
const mapDispatchToProps = dispatch => ({
  getLayers: () => dispatch(getLayers()),
  handleFineGranularityChange: changes => {
    dispatch(handleFineGranularityChange(changes));
  }
});
const mapStateToProps = ({ rolesPermissions, layers }) => ({
  fineGranularityChanges: rolesPermissions.fineGranularityChanges,
  loading: layers.loading,
  layers: layers.layers
});

export default connect(mapStateToProps, mapDispatchToProps)(FineGranularityModal);
